import { jsx as _jsx } from "react/jsx-runtime";
import isEqual from "lodash/isEqual";
import { memo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useGetPublicAssetMenuOptionsCallback } from "~/components/PublicBoard/useGetPublicAssetMenuOptionsCallback";
import { usePublicPermissions } from "~/components/PublicPermissionsProvider";
import { GalleryAsset } from "~/components/v2/components/shared/gallery/GalleryAsset";
import { usePublicGetAvailableCustomFields } from "~/components/v2/hooks/usePublicGetAvailableCustomFields";
import { QueryParamNames } from "~/constants/search";
import { useGoToAssetPage } from "~/hooks/useGoToAssetPage";
import { useOpenAssetInNewTab } from "~/hooks/useOpenAssetInNewTab";
import { usePublicSelectionMenuOptions } from "~/hooks/usePublicSelectionMenuOptions";
import { useURLLibraryIdSelector } from "~/hooks/useURLLibraryIdSelector";
import { AssetModalPanel } from "~/providers/AssetModalPanelContextProvider";
import { usePublicCustomFieldsContext } from "~/providers/PublicCustomFieldsProvider";
import { centralizedBoardLibraryIdSelector } from "~/store/centralizedBoard/selectors";
import { canSeeAssetCustomFields, canViewAssetDiscussions } from "~/utils/permissions/assetPermissions";
export const PublicGalleryAsset = /*#__PURE__*/ memo((param)=>{
    let { data: asset, box } = param;
    const { permissions } = usePublicPermissions();
    const { customFields } = usePublicCustomFieldsContext();
    const { getSelectionMenuOptions } = usePublicSelectionMenuOptions();
    const { getAssetMenuOptionsCallback } = useGetPublicAssetMenuOptionsCallback();
    const _getItemMenuOptions = getAssetMenuOptionsCallback({
        item: asset
    });
    const { goToAssetPage } = useGoToAssetPage();
    const { openAssetInNewTab } = useOpenAssetInNewTab();
    const urlLibraryId = useURLLibraryIdSelector();
    const libraryId = useSelector(centralizedBoardLibraryIdSelector);
    const { getAvailableCustomFields } = usePublicGetAvailableCustomFields({
        libraryId: libraryId || urlLibraryId
    });
    const onCustomFieldsClick = useCallback(()=>goToAssetPage({
            asset,
            query: {
                [QueryParamNames.tab]: AssetModalPanel.FIELDS
            },
            trackLocation: "custom-field-click"
        }), [
        asset,
        goToAssetPage
    ]);
    const onDiscussionsClick = useCallback(()=>goToAssetPage({
            asset,
            query: {
                [QueryParamNames.tab]: AssetModalPanel.COMMENTS
            },
            trackLocation: "comments-click"
        }), [
        asset,
        goToAssetPage
    ]);
    const onVersionsClick = useCallback(()=>goToAssetPage({
            asset,
            query: {
                [QueryParamNames.versions]: true
            },
            trackLocation: "versions-click"
        }), [
        asset,
        goToAssetPage
    ]);
    const onItemClick = useCallback(()=>goToAssetPage({
            asset,
            trackLocation: "card-click"
        }), [
        asset,
        goToAssetPage
    ]);
    const onItemCmdClick = useCallback(()=>openAssetInNewTab({
            asset,
            trackLocation: "card-click"
        }), [
        asset,
        openAssetInNewTab
    ]);
    const canViewCustomFields = canSeeAssetCustomFields(permissions);
    const canSeeDiscussions = canViewAssetDiscussions(permissions);
    return /*#__PURE__*/ _jsx(GalleryAsset, {
        height: box.height,
        width: box.width,
        clip: asset,
        isSelectable: true,
        onAssetClick: onItemClick,
        onAssetTitleCmdClick: onItemCmdClick,
        onCustomFieldsClick: onCustomFieldsClick,
        onDiscussionsClick: onDiscussionsClick,
        onVersionsClick: onVersionsClick,
        showVersionsIndicator: !!asset.assetVersionCount && (asset === null || asset === void 0 ? void 0 : asset.assetVersionCount) > 1,
        getItemMenuOptions: _getItemMenuOptions,
        getSelectionMenuOptions: getSelectionMenuOptions,
        canViewCustomFields: canViewCustomFields,
        allCustomFields: getAvailableCustomFields(customFields),
        shouldShowDiscussions: canSeeDiscussions
    }, asset.id);
}, // `container` comes from `react-virtualized` and is a different object every time but may have the same values so we need to deep equal check the props
isEqual);
PublicGalleryAsset.displayName = "PublicGalleryAsset";
